import { type FC, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Modal } from '../../../../components/Modal/Modal'
import {
  type AccountSchedulingSettings,
  EventTypeAccess,
  type Location,
  LocationKind,
  type LocationRef,
  type MeetingMode,
  type SchedulingEventType,
} from '../../../../models/Scheduling'
import { PlaceToggleCards } from '../../../Scheduling/flows/form/PlaceToggleCards'
import { DURATION_OPTIONS } from '../../../Scheduling/shared/eventTypeOptions'

interface EditEventTypeModalProps {
  eventType: SchedulingEventType
  accountSettings?: AccountSchedulingSettings
  locations: Location[]
  onSave: (patch: Partial<SchedulingEventType>) => void
  onCancel: () => void
}

const COPY: Record<EventTypeAccess, { title: string; subtitle: string }> = {
  [EventTypeAccess.ACCOUNT]: {
    title: 'Edit shared event type',
    subtitle: 'Visible to everyone in your account. Uses account defaults you can change later.',
  },
  [EventTypeAccess.PERSONAL]: {
    title: 'Edit personal event type',
    subtitle: 'Only you can offer this event type. Uses account defaults you can change later.',
  },
}

export const EditEventTypeModal: FC<EditEventTypeModalProps> = ({ eventType, accountSettings, locations, onSave, onCancel }) => {
  const [name, setName] = useState(eventType.name)
  const [durationMinutes, setDurationMinutes] = useState(eventType.durationMinutes)
  const [meetingMode, setMeetingMode] = useState<MeetingMode>(eventType.meetingMode)
  const [location, setLocation] = useState<LocationRef | null>(eventType.location ?? null)
  const [description, setDescription] = useState(eventType.description ?? '')

  const defaultLocation = locations.find((location) => location.id === accountSettings?.defaults.locationId)
  const copy = COPY[eventType.access]

  const handleSave = () => {
    onSave({
      name: name.trim(),
      durationMinutes,
      meetingMode,
      location,
      description,
    })
  }

  return (
    <Modal
      show
      onHide={onCancel}
      header={
        <div className='d-flex flex-column zocks-gap-0.25'>
          <h5 className='mb-0'>{copy.title}</h5>
          <span className='paragraph-small zocks-text-gray-600'>{copy.subtitle}</span>
        </div>
      }
      headerBodySeparator
      body={
        <div className='d-flex flex-column gap-3'>
          <div className='d-flex flex-column zocks-gap-0.25'>
            <label className='bold' htmlFor='edit-event-type-name'>
              Name
            </label>
            <span className='paragraph-small zocks-text-gray-600'>Clients see this when they book.</span>
            <Form.Control id='edit-event-type-name' type='text' value={name} onChange={(event) => setName(event.currentTarget.value)} />
          </div>
          <div className='d-flex flex-column zocks-gap-0.25'>
            <label className='bold' htmlFor='edit-event-type-duration'>
              Duration
            </label>
            <Form.Select id='edit-event-type-duration' value={durationMinutes} onChange={(event) => setDurationMinutes(Number(event.currentTarget.value))}>
              {DURATION_OPTIONS.map((minutes) => (
                <option key={minutes} value={minutes}>
                  {minutes} minutes
                </option>
              ))}
            </Form.Select>
          </div>
          <PlaceToggleCards
            meetingMode={meetingMode}
            defaultLocation={defaultLocation}
            onChange={(mode, locId) => {
              setMeetingMode(mode)
              setLocation(locId ? { kind: LocationKind.FIXED, id: locId } : null)
            }}
          />
          <div className='d-flex flex-column zocks-gap-0.25'>
            <label className='bold' htmlFor='edit-event-type-description'>
              Description <span className='zocks-text-gray-500'>(optional)</span>
            </label>
            <span className='paragraph-small zocks-text-gray-600'>Only your team sees this.</span>
            <Form.Control
              as='textarea'
              id='edit-event-type-description'
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.currentTarget.value)}
            />
          </div>
        </div>
      }
      showFooterActions
      footerPrimaryActionTitle='Save changes'
      footerSecondaryActionTitle='Cancel'
      footerPrimaryActionDisabled={!name.trim()}
      onFooterPrimaryAction={handleSave}
      onFooterSecondaryAction={onCancel}
    />
  )
}
